/* eslint-disable @typescript-eslint/no-restricted-imports */
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  DtoV1PropertyTaxEscrowDetails,
  DtoV1PropertyTaxSaveEscrowDetailsRequestDto,
} from '~/__generated__/api';
import GlobalLayout from '~/common/components/GlobalLayout';
import QuestionLibraryGlobalLayout from '~/common/components/QuestionLibrary/layouts/QuestionLibraryGlobalLayout';
import { LOAN_SUMMARY_STATUS_BY_NAME, USER_ROLE } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import useGetLoanSummary, {
  type LoanSummary,
} from '~/common/hooks/useGetLoanSummary';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import { useSelectedAlert } from '~/stores/navigationStore';

import {
  formatSaveData,
  generateScreenData,
  useDocumentOptions,
} from './common/helpers';
import {
  useAdditionalTaxesAlertMessage,
  useDelinquentExceedsAcceptableThreshold,
  useDelinquentInputHasValueAlert,
  useDelinquentTaxAmount,
  useDelinquentTaxInputExceedsThresholdAlert,
  useDisplayDelinquentAlert,
  useIsExemptToggle,
  useManageSupplementalTaxRows,
  useMillageRateValue,
  usePastTaxesDelinquentAlert,
  useResetUnequalInstallmentsWhenMillageRateToggled,
  useTotalAmount,
  useUpdateAnnualAmountLabel,
  useUpdateDisbursementLabels,
  useUpdateFutureInstallmentsToMatchEscrowInstallmentAmount,
  useUpdateFutureInstallmentsToMatchEscrowInstallmentsIfMonthAndDayAreTheSame,
  useUpdateInstallmentAmountsWhenUnequalToggle,
} from './common/hooks/sideEffects';
import { useIndividualTaxData } from './common/hooks/useTaxesData';
import type { TaxesFormValues } from './common/taxFormUtils';
import json from './layouts/taxesQuestionData.json';

type TaxesProps = {
  taxId?: string;
};

export default function Taxes({ taxId }: Readonly<TaxesProps>) {
  const { isLoading, isError, data, saveTaxes } = useIndividualTaxData(taxId);
  const {
    data: loanData,
    isLoading: isLoanSummaryLoading,
    isError: loanSummaryError,
  } = useGetLoanSummary();

  if (
    isLoading ||
    isError ||
    !data ||
    isLoanSummaryLoading ||
    loanSummaryError
  ) {
    return (
      <GlobalLayout
        isLoading={isLoading || isLoanSummaryLoading}
        error={
          (isError || loanSummaryError) && 'There was an issue loading Taxes'
        }
        primaryButton={{
          isDisabled: true,
        }}
      />
    );
  }

  return (
    <TaxesContent
      saveTaxes={saveTaxes}
      data={data}
      loanSummaryInfo={loanData}
    />
  );
}

type TaxesContentProps = {
  saveTaxes: (
    data: Omit<
      DtoV1PropertyTaxSaveEscrowDetailsRequestDto,
      'obfuscatedLoanIdentifier'
    >,
  ) => void;
  data: DtoV1PropertyTaxEscrowDetails;
  loanSummaryInfo: LoanSummary | undefined;
};

const TaxesContent = ({
  saveTaxes,
  data,
  loanSummaryInfo,
}: TaxesContentProps) => {
  const displayTitle = useSelectedAlert((alert) => alert.displayTitle);
  const { isUnderwriter, role, isCloser, isTracUser } = useUser();

  const methods = useForm({
    mode: 'onBlur',
  });

  const { documentId, documentOptions } = useDocumentOptions(
    data?.taxDocuments,
  );

  const { ArePropertyTaxesIconsEnabled: arePropertyTaxesIconsEnabled } =
    useNavFeatureFlags();

  const screenData = useMemo(() => {
    const isCloserTl = role.includes(USER_ROLE.CLOSER_TL);
    const canUpdateInstallments = isCloserTl || isUnderwriter || isTracUser;
    const canUpdateFutureInstallments = isCloser || isCloserTl;
    const taxScreenData = generateScreenData({
      loanSummaryInfo,
      data,
      arePropertyTaxesIconsEnabled,
    });
    const loanStatusId = loanSummaryInfo?.loanInfo.loanStatusId;
    const isPostClosing =
      loanStatusId === LOAN_SUMMARY_STATUS_BY_NAME.POST_CLOSED ||
      loanStatusId === LOAN_SUMMARY_STATUS_BY_NAME.POST_CLOSE_PROBLEM;
    const canUpdatePastInstallments =
      isCloserTl || isUnderwriter || isCloser || (isTracUser && isPostClosing);
    return {
      ...taxScreenData,
      isTaxDollarAmountRestricted:
        taxScreenData.isTracLoan && isTracUser && !isPostClosing,
      isCloser,
      isCloserTl,
      canUpdateInstallments,
      canUpdateFutureInstallments,
      canUpdatePastInstallments,
    };
  }, [
    data,
    loanSummaryInfo,
    isUnderwriter,
    isCloser,
    isTracUser,
    role,
    arePropertyTaxesIconsEnabled,
  ]);

  useEffect(() => {
    if (data && screenData?.initialData) {
      methods.reset(screenData.initialData);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [data]);

  const onSubmit = async (values: TaxesFormValues) => {
    const postData = formatSaveData({ values, data });
    saveTaxes({
      ...postData,
      escrowDetails: {
        ...postData.escrowDetails,
        millageRate:
          postData?.escrowDetails?.millageRate == null ||
          postData.escrowDetails.millageRate.toString().trim() === ''
            ? null
            : postData.escrowDetails.millageRate,
      },
    });
  };

  return (
    <QuestionLibraryGlobalLayout
      headerOptions={{
        title: displayTitle,
        isAiSwitchButtonEnabled: false,
        isHidden: true,
      }}
      questions={json}
      showPdfViewer={true}
      documentId={documentId}
      documentOptions={documentOptions}
      screenData={screenData}
      sideEffects={[
        useUpdateInstallmentAmountsWhenUnequalToggle,
        useResetUnequalInstallmentsWhenMillageRateToggled,
        useMillageRateValue,
        useDelinquentTaxAmount,
        useTotalAmount,
        useIsExemptToggle,
        useUpdateFutureInstallmentsToMatchEscrowInstallmentsIfMonthAndDayAreTheSame,
        useUpdateFutureInstallmentsToMatchEscrowInstallmentAmount,
        useUpdateAnnualAmountLabel,
        useUpdateDisbursementLabels,
        useDisplayDelinquentAlert,
        useDelinquentExceedsAcceptableThreshold,
        usePastTaxesDelinquentAlert,
        useDelinquentTaxInputExceedsThresholdAlert,
        useDelinquentInputHasValueAlert,
        useAdditionalTaxesAlertMessage,
        useManageSupplementalTaxRows,
      ]}
      hasNewBackground
      shouldUpdateScreenData={true}
      maxHeight={true}
      gridTemplateColumns="50% 50%"
      methods={methods}
      primaryButton={{
        onSubmit,
      }}
      fullPageHeight={true}
    />
  );
};
